'use client'

import { useEffect } from 'react'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { DesktopSearchDropdown } from './DesktopSearchDropdown'
import { MobileSearch } from './MobileSearch'
import { SearchInputField } from './SearchInputField'
import {
    type ResolvedSearchPlacement,
    type SearchPlacement,
    type SearchTermSuggestion,
} from './universalSearchTypes'
import { DEFAULT_TERM_SUGGESTIONS } from './universalSearchDefaults'
import { useUniversalSearch } from './useUniversalSearch'
import classes from './UniversalSearch.module.css'

export interface UniversalSearchProps {
    /** Hint text shown when the search field is empty. */
    placeholder: string
    /** Shorter hint text for narrow screens, where `placeholder` would clip. Defaults to `placeholder`. */
    mobilePlaceholder?: string
    /** Accessible name for the search field. Defaults to "Search". */
    ariaLabel?: string
    /**
     * Text to show in the field. Does not submit a search or open typeahead.
     */
    searchQuery?: string
    /** Starter chips shown when the search field is empty. */
    termSuggestions?: SearchTermSuggestion[]
    /** Runs when the user dismisses the desktop dropdown or mobile panel. */
    onClose?: () => void
    /**
     * Where the closed search bar sits on screen.
     * - `inline` — in the page flow (header or category). Focusing it on desktop
     *   opens a dropdown; on mobile the field moves to the bottom with suggestions above it.
     * - `dock` — pinned to the bottom of the screen. Focusing it opens suggestions above the field.
     * - `auto` — `dock` on small screens, `inline` on desktop.
     * - `trigger` — `inline` on desktop; hidden on small screens until opened from outside.
     */
    placement: SearchPlacement
    /** Value sent as the analytics location for search events. */
    analyticsLocation?: string
    /**
     * Publishes a function that opens the search by focusing its input, and clears it on unmount.
     * Lets controls outside the search open it from a click handler.
     */
    onRegisterOpener?: (opener: (() => void) | null) => void
}

/** Placement after `auto` and `trigger` are resolved for the current screen size. */
function resolvePlacement(placement: SearchPlacement, isMobile: boolean): ResolvedSearchPlacement {
    if (placement === 'auto') return isMobile ? 'dock' : 'inline'
    if (placement === 'trigger') return isMobile ? 'trigger' : 'inline'
    return placement
}

/**
 * Search input with starter suggestions and live text suggestions.
 *
 * How the component renders and displays on the page is highly dependent on the `placement` prop.
 */
export function UniversalSearch({
    placeholder,
    mobilePlaceholder = placeholder,
    ariaLabel,
    searchQuery,
    termSuggestions = DEFAULT_TERM_SUGGESTIONS,
    onClose,
    placement,
    analyticsLocation,
    onRegisterOpener,
}: UniversalSearchProps) {
    const { isSm: isMobile, isReady } = useBreakpoint()
    const resolvedPlacement = resolvePlacement(placement, isMobile)
    const search = useUniversalSearch({
        searchQuery,
        onClose,
        analyticsLocation,
    })
    const { inputRef, handleFocus } = search

    useEffect(() => {
        if (!onRegisterOpener || !isReady) return

        onRegisterOpener(() => {
            const input = inputRef.current
            if (!input) return

            // focus() does nothing on an already-focused input (for example after Escape), so open directly.
            if (document.activeElement === input) {
                handleFocus()
                return
            }

            // Scrolling would close the desktop dropdown, which dismisses on any scroll.
            input.focus({ preventScroll: true })
        })

        return () => onRegisterOpener(null)
    }, [onRegisterOpener, isReady, inputRef, handleFocus])

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            search.submit()
        }

        if (event.key === 'Escape') {
            event.preventDefault()
            search.inputRef.current?.blur()
            search.close()
        }
    }

    if (isMobile) {
        return (
            <MobileSearch
                placeholder={mobilePlaceholder}
                ariaLabel={ariaLabel}
                termSuggestions={termSuggestions}
                search={search}
                onKeyDown={handleKeyDown}
                resolvedPlacement={resolvedPlacement}
            />
        )
    }

    const collapsedInput = (
        <SearchInputField
            variant="inlineClosed"
            value={search.query}
            placeholder={placeholder}
            ariaLabel={ariaLabel}
            onChange={search.setQuery}
            onFocus={search.handleFocus}
            onKeyDown={handleKeyDown}
            inputRef={search.inputRef}
        />
    )

    return (
        <div className={`${classes.root} ${placement === 'auto' ? classes.autoPlacement : ''}`}>
            <DesktopSearchDropdown
                target={
                    <div
                        className={`${classes.collapsedTrigger} ${search.isOpen ? classes.collapsedTriggerHidden : ''}`}
                        aria-hidden={search.isOpen || undefined}
                    >
                        {collapsedInput}
                    </div>
                }
                placeholder={placeholder}
                ariaLabel={ariaLabel}
                termSuggestions={termSuggestions}
                search={search}
                onKeyDown={handleKeyDown}
            />
        </div>
    )
}
